import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import classNames from 'classnames';
import { useTranslation, Trans } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Button,
  Input,
  Heading,
  Text,
  InputWithIcon,
} from '@quality24/design-system';
import { faQrcode } from '@fortawesome/pro-regular-svg-icons';
import {
  CodeScanner,
  CodeScannerProps,
} from '@quality24/react-order-components';
import RadioField from '../../molecules/RadioField';

import styles from './LoginProcessForm.module.scss';

export interface Props {
  onSubmit?: (values: {
    userType: string;
    name: string;
    birthDate: string;
    admissionNumber: string;
  }) => void;
  className?: string;
  validations?: string[];
}

interface FormValues {
  userType: string;
  admissionNumber: string;
  name: string;
  birthDate: string;
}

/*
 *
 * Validation Schema
 */

const validationSchema = (validations: string[], t: TFunction) =>
  Yup.object({
    name: Yup.string().when('userType', {
      is: 'PATIENT_COMPANION',
      then: (schema) => schema.required(t('validation.required')),
      // otherwise: (schema) => schema.string(),
    }),
    admissionNumber: Yup.string().required(t('validation.required')),
    birthDate: validations.includes('birthDate')
      ? Yup.string().required(t('validation.required'))
      : Yup.string().nullable(),
    userType: Yup.string().oneOf(['PATIENT_COMPANION', 'PATIENT']),
  });

const LoginProcessForm: React.FunctionComponent<Props> = ({
  onSubmit = () => {},
  className = '',
  validations = [],
}) => {
  const { t } = useTranslation();
  const [isCodeScannerOpen, setIsCodeScannerOpen] = React.useState(false);

  const options = [
    { label: t('welcome.userType.patient'), value: 'PATIENT' },
    { label: t('welcome.userType.companion'), value: 'PATIENT_COMPANION' },
  ];

  const defaultValues: FormValues = {
    userType: '',
    admissionNumber: '',
    name: '',
    birthDate: '',
  };

  const schema = React.useMemo(
    () => validationSchema(validations, t),
    [validations, t],
  );

  const formik = useFormik<FormValues>({
    initialValues: defaultValues,
    validationSchema: schema,
    onSubmit,
  });

  const handleCodeScannerResult = React.useCallback<
    NonNullable<CodeScannerProps['onResult']>
  >(
    (result, error) => {
      if (error) {
        return;
      }

      if (!result) {
        return;
      }

      const text = result.getText();
      if (!text) {
        return;
      }

      // Check if all numbers or P/p + numbers
      const isValidFormat = /^\d+$/.test(text) || /^[Pp]\d+$/.test(text);

      if (isValidFormat) {
        formik.setFieldValue('admissionNumber', text.replace(/^[Pp]/, ''));
        setIsCodeScannerOpen(false);
      }
    },
    [formik],
  );
  const cls = classNames(className, 'h-100');

  const handleOpenCodeScanner = React.useCallback(
    () => setIsCodeScannerOpen(true),
    [],
  );

  return (
    <>
      <form autoComplete="off" onSubmit={formik.handleSubmit} className={cls}>
        <div>
          {!formik.values.userType && (
            <>
              <Heading as="h2" size="xl" weight="bold" className="mb-2">
                {t('welcome.title')}
              </Heading>
              <Text>
                <Trans
                  i18nKey="welcome.body"
                  components={{ strong: <strong /> }}
                />
              </Text>
              <Text className="mt-4">{t('welcome.bodyExtra')}</Text>
              <div className={styles.navigation}>
                <RadioField
                  className={classNames(styles.button, 'w-100 text-uppercase')}
                  type="button"
                  direction="column"
                  name="userType"
                  controlId="userType"
                  value={formik.values.userType}
                  onChange={(_, value) => {
                    formik.setValues({
                      userType: value,
                      admissionNumber: '',
                      name: '',
                      birthDate: '',
                    });
                    formik.setTouched({ admissionNumber: false, name: false });
                    // setUserType(value);
                  }}
                  options={options}
                  optionsClassName="text-center"
                  size="lg"
                  fluid
                />
              </div>
            </>
          )}

          {formik.values.userType === 'PATIENT_COMPANION' && (
            <>
              <Heading as="h2" size="xl" weight="bold" className="mb-2">
                {t('login.almostThere')}
              </Heading>
              <Text>
                <Trans
                  i18nKey="login.companionInstructions"
                  components={{ strong: <strong /> }}
                />
              </Text>
              <div className="d-flex flex-column gap-3 mt-3">
                <Input
                  id="name"
                  type="text"
                  placeholder={t('login.fields.name')}
                  name="name"
                  value={formik.values.name}
                  helperText={formik.errors.name}
                  onChange={formik.handleChange}
                />

                <InputWithIcon
                  id="admissionNumber"
                  type="text"
                  placeholder={t('login.fields.admission')}
                  name="admissionNumber"
                  appendIcon={faQrcode}
                  onAppendClick={handleOpenCodeScanner}
                  value={formik.values.admissionNumber}
                  helperText={
                    formik.touched.admissionNumber
                      ? formik.errors.admissionNumber
                      : ''
                  }
                  onChange={formik.handleChange}
                />

                {validations.includes('birthDate') && (
                  <Input
                    id="birthDate"
                    type="date"
                    label={t('login.fields.companionBirthDate')}
                    name="birthDate"
                    value={formik.values.birthDate}
                    helperText={
                      formik.touched.birthDate ? formik.errors.birthDate : ''
                    }
                    onChange={formik.handleChange}
                  />
                )}
              </div>
            </>
          )}

          {formik.values.userType === 'PATIENT' && (
            <>
              <Heading as="h2" size="xl" weight="bold" className="mb-2">
                {t('login.almostThere')}
              </Heading>
              <Text>
                <Trans
                  i18nKey="login.patientInstructions"
                  components={{ strong: <strong /> }}
                />
              </Text>

              <div className="d-flex flex-column gap-3 mt-3">
                <InputWithIcon
                  id="admissionNumber"
                  type="text"
                  placeholder={t('login.fields.admission')}
                  name="admissionNumber"
                  appendIcon={faQrcode}
                  onAppendClick={handleOpenCodeScanner}
                  value={formik.values.admissionNumber}
                  helperText={
                    formik.touched.admissionNumber
                      ? formik.errors.admissionNumber
                      : ''
                  }
                  onChange={formik.handleChange}
                />

                {validations.includes('birthDate') && (
                  <Input
                    id="birthDate"
                    type="date"
                    label={t('login.fields.patientBirthDate')}
                    name="birthDate"
                    value={formik.values.birthDate}
                    helperText={
                      formik.touched.birthDate ? formik.errors.birthDate : ''
                    }
                    onChange={formik.handleChange}
                  />
                )}
              </div>
            </>
          )}
        </div>

        {formik.values.userType && (
          <div className={styles.navigation}>
            <Button type="submit" size="lg" fluid>
              {t('common.submit')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => formik.setFieldValue('userType', '')}
              fluid
            >
              {t('common.back')}
            </Button>
          </div>
        )}
      </form>
      {isCodeScannerOpen && (
        <CodeScanner
          onResult={handleCodeScannerResult}
          fullscreen
          onClose={() => setIsCodeScannerOpen(false)}
        />
      )}
    </>
  );
};

export default LoginProcessForm;
